import Debug from 'debug';
import { Buffer } from 'buffer';
import { STEEmitter } from './stringly-typed-event-emitter';
var MediaPeerCommands;
(function (MediaPeerCommands) {
    MediaPeerCommands[MediaPeerCommands["SegmentData"] = 0] = "SegmentData";
    MediaPeerCommands[MediaPeerCommands["SegmentAbsent"] = 1] = "SegmentAbsent";
    MediaPeerCommands[MediaPeerCommands["SegmentsMap"] = 2] = "SegmentsMap";
    MediaPeerCommands[MediaPeerCommands["SegmentsPending"] = 3] = "SegmentsPending";
    MediaPeerCommands[MediaPeerCommands["SegmentRequest"] = 4] = "SegmentRequest";
    MediaPeerCommands[MediaPeerCommands["CancelSegmentRequest"] = 5] = "CancelSegmentRequest";
})(MediaPeerCommands || (MediaPeerCommands = {}));
class DownloadingSegment {
    constructor(id, size) {
        this.id = id;
        this.size = size;
        this.bytesDownloaded = 0;
        this.pieces = [];
        this.loading = { start: 0, end: 0, first: 0 };
    }
}
export class MediaPeer extends STEEmitter {
    constructor(peer, settings) {
        super();
        this.peer = peer;
        this.settings = settings;
        this.remoteAddress = '';
        this.downloadingSegmentId = null;
        this.downloadingSegment = null;
        this.segmentsMap = [];
        this.pendingStatus = [];
        this.lastSegmentsSend_ = '';
        this.debug = Debug('p2pml:media-peer');
        this.timer = null;
        this.onPeerConnect = () => {
            this.debug('peer connect', this.id, this);
            this.remoteAddress = this.peer.remoteAddress;
            this.emit('connect', this);
        };
        this.onPeerClose = () => {
            this.debug('peer close', this.id, this);
            this.terminateSegmentRequest();
            this.emit('close', this);
        };
        this.onPeerError = (error) => {
            this.debug('peer error', this.id, error, this);
        };
        this.receiveSegmentPiece = (data) => {
            if (!this.downloadingSegment) {
                this.debug('peer segment not requested', this.id, this);
                return;
            }
            if (this.downloadingSegment.loading.first === 0) {
                this.downloadingSegment.loading.first = performance.now();
            }
            this.downloadingSegment.bytesDownloaded += data.byteLength;
            this.downloadingSegment.pieces.push(data);
            this.emit('bytes-downloaded', this, this.downloadingSegment.id, data.byteLength);
            const segmentId = this.downloadingSegment.id;
            this.emit('segment-downloaded', this.downloadingSegment);
            if (this.downloadingSegment.bytesDownloaded === this.downloadingSegment.size) {
                const segmentData = new Uint8Array(this.downloadingSegment.size);
                let offset = 0;
                for (const piece of this.downloadingSegment.pieces) {
                    segmentData.set(new Uint8Array(piece), offset);
                    offset += piece.byteLength;
                }
                this.downloadingSegment.loading.end = performance.now();
                this.debug('peer segment download done', this.id, segmentId, this);
                this.terminateSegmentRequest();
                this.emit('segment-loaded', this, segmentId, segmentData.buffer);
            }
            else if (this.downloadingSegment.bytesDownloaded > this.downloadingSegment.size) {
                this.debug('peer segment download bytes mismatch', this.id, segmentId, this);
                this.terminateSegmentRequest();
                this.emit('segment-error', this, segmentId, 'Too many bytes received for segment');
            }
        };
        this.getJsonCommand = (data) => {
            const bytes = new Uint8Array(data);
            if (bytes[0] === 123 && bytes[1] === 34 && bytes[data.byteLength - 1] === 125) {
                try {
                    return JSON.parse(new TextDecoder().decode(data));
                }
                catch (_a) {
                    return null;
                }
            }
            return null;
        };
        this.onPeerData = (data) => {
            const command = this.getJsonCommand(data);
            if (command === null) {
                this.receiveSegmentPiece(data);
                return;
            }
            if (this.downloadingSegment) {
                this.debug('peer segment download is interrupted by a command', this.id, this);
                const segmentId = this.downloadingSegment.id;
                this.terminateSegmentRequest();
                this.emit('segment-error', this, segmentId, 'Segment download is interrupted by a command');
                return;
            }
            this.debug('peer receive command', this.id, command);
            switch (command.c) {
                case MediaPeerCommands.SegmentsMap:
                    this.segmentsMap = command.m.split('|');
                    this.emit('data-updated', { peerId: this.peer.id, segmentsMap: this.segmentsMap });
                    break;
                case MediaPeerCommands.SegmentRequest:
                    this.emit('segment-request', this, command.i);
                    break;
                case MediaPeerCommands.SegmentData:
                    if (this.downloadingSegmentId && this.downloadingSegmentId === command.i) {
                        if (typeof command.s === 'number' && command.s >= 0) {
                            this.downloadingSegment = new DownloadingSegment(command.i, command.s);
                            this.cancelResponseTimeoutTimer();
                            this.runResponseTimeoutTimer();
                        }
                        else {
                            this.debug('invalid segment size', this.id, command.s, this);
                        }
                    }
                    break;
                case MediaPeerCommands.SegmentsPending:
                    try {
                        this.pendingStatus = command.m.split('|');
                    }
                    catch (error) { }
                    break;
                case MediaPeerCommands.SegmentAbsent:
                    if (this.downloadingSegmentId && this.downloadingSegmentId === command.i) {
                        this.terminateSegmentRequest();
                        this.segmentsMap = this.segmentsMap.filter((x) => x !== command.i);
                        this.emit('segment-absent', this, command.i);
                    }
                    break;
                case MediaPeerCommands.CancelSegmentRequest:
                    break;
                default:
                    break;
            }
        };
        this.sendCommand = (command) => {
            this.peer.write(JSON.stringify(command));
        };
        this.destroy = () => {
            this.debug('peer destroy', this.id, this);
            this.terminateSegmentRequest();
            this.peer.destroy();
        };
        this.getDownloadingSegmentId = () => {
            return this.downloadingSegmentId;
        };
        this.checkPendingTimeout = (segmentId) => {
            try {
                if (this.pendingStatus[0] === segmentId) {
                    return Number(this.pendingStatus[1]);
                }
                return 0;
            }
            catch (e) {
                return 0;
            }
        };
        this.getSegmentsMap = () => {
            return this.segmentsMap;
        };
        this.getPendingStatus = () => {
            return this.pendingStatus;
        };
        this.sendSegmentsMap = (segmentsMap) => {
            if (this.lastSegmentsSend_ === segmentsMap)
                return;
            this.lastSegmentsSend_ = segmentsMap;
            this.sendCommand({ c: MediaPeerCommands.SegmentsMap, m: segmentsMap });
        };
        this.sendSegmentsPending = (pendingStatus) => {
            this.sendCommand({ c: MediaPeerCommands.SegmentsPending, m: pendingStatus });
        };
        this.sendSegmentData = (segmentId, data) => {
            this.sendCommand({
                c: MediaPeerCommands.SegmentData,
                i: segmentId,
                s: data.byteLength,
            });
            let bytesLeft = data.byteLength;
            while (bytesLeft > 0) {
                const bytesToSend = bytesLeft >= this.settings.webRtcMaxMessageSize ? this.settings.webRtcMaxMessageSize : bytesLeft;
                const buffer = Buffer.from(data, data.byteLength - bytesLeft, bytesToSend);
                this.peer.write(buffer);
                bytesLeft -= bytesToSend;
            }
            this.emit('bytes-uploaded', this, data.byteLength);
        };
        this.sendSegmentAbsent = (segmentId) => {
            this.sendCommand({ c: MediaPeerCommands.SegmentAbsent, i: segmentId });
        };
        this.requestSegment = (segmentId) => {
            if (!segmentId) {
                throw new Error('segmentId is empty');
            }
            if (this.downloadingSegmentId === segmentId) {
                throw new Error('Target segment is already downloading: ' + segmentId);
            }
            if (this.downloadingSegmentId) {
                throw new Error('A segment is already downloading: ' + this.downloadingSegmentId);
            }
            this.sendCommand({ c: MediaPeerCommands.SegmentRequest, i: segmentId });
            this.downloadingSegmentId = segmentId;
            this.runResponseTimeoutTimer();
        };
        this.cancelSegmentRequest = () => {
            let downloadingSegment;
            if (this.downloadingSegmentId) {
                const segmentId = this.downloadingSegmentId;
                downloadingSegment = this.downloadingSegment ? this.downloadingSegment.pieces : undefined;
                this.terminateSegmentRequest();
                this.sendCommand({ c: MediaPeerCommands.CancelSegmentRequest, i: segmentId });
            }
            return downloadingSegment;
        };
        this.runResponseTimeoutTimer = () => {
            this.timer = setTimeout(() => {
                this.timer = null;
                if (!this.downloadingSegmentId) {
                    return;
                }
                const segmentId = this.downloadingSegmentId;
                this.cancelSegmentRequest();
                this.emit('segment-timeout', this, segmentId);
            }, this.settings.p2pSegmentDownloadTimeout);
        };
        this.cancelResponseTimeoutTimer = () => {
            if (this.timer) {
                clearTimeout(this.timer);
                this.timer = null;
            }
        };
        this.terminateSegmentRequest = () => {
            this.downloadingSegmentId = null;
            this.downloadingSegment = null;
            this.cancelResponseTimeoutTimer();
        };
        this.peer.on('connect', this.onPeerConnect);
        this.peer.on('close', this.onPeerClose);
        this.peer.on('error', this.onPeerError);
        this.peer.on('data', this.onPeerData);
        this.id = peer.id;
    }
    canDownload(segmentId) {
        const canDownload = this.downloadingSegmentId === null && this.segmentsMap.includes(segmentId);
        return canDownload;
    }
}
